/**
 * Hook for managing bulk file conversion workflow.
 */

import { useState, useCallback } from 'react';
import { useConversionStore } from '@stores/conversionStore';
import { validateCubeFile, isValidationValid, isConvertSuccess } from '@wasm/index';
import { convertContent, readFileAsText, generateOutputFilename } from '@services/converter';
import { createZip, generateZipFilename, type ZipFile } from '@services/zipBuilder';

export interface BulkConversionProgress {
  completed: number;
  total: number;
  currentFile: string | null;
}

export interface BulkConversionResult {
  succeeded: number;
  failed: number;
  zipBlob: Blob | null;
  zipFilename: string;
}

export interface UseBulkConversionReturn {
  /** Current progress */
  progress: BulkConversionProgress;
  /** Whether conversion is in progress */
  isConverting: boolean;
  /** Final result after conversion completes */
  result: BulkConversionResult | null;
  /** Start bulk conversion */
  convertAll: () => Promise<void>;
  /** Reset to initial state */
  reset: () => void;
}

export function useBulkConversion(): UseBulkConversionReturn {
  const files = useConversionStore((s) => s.files);
  const options = useConversionStore((s) => s.options);
  const updateFileStatus = useConversionStore((s) => s.updateFileStatus);
  const setFileResult = useConversionStore((s) => s.setFileResult);
  const startJob = useConversionStore((s) => s.startJob);
  const completeJob = useConversionStore((s) => s.completeJob);

  const [progress, setProgress] = useState<BulkConversionProgress>({
    completed: 0,
    total: 0,
    currentFile: null,
  });
  const [isConverting, setIsConverting] = useState(false);
  const [result, setResult] = useState<BulkConversionResult | null>(null);

  const convertAll = useCallback(async () => {
    if (files.length === 0) return;

    setIsConverting(true);
    setResult(null);
    startJob();

    const total = files.length;
    let completed = 0;
    let succeeded = 0;
    let failed = 0;
    const convertedFiles: ZipFile[] = [];

    setProgress({ completed: 0, total, currentFile: null });

    for (const file of files) {
      setProgress((prev) => ({ ...prev, currentFile: file.filename }));

      try {
        // Update status to converting
        updateFileStatus(file.id, 'converting');

        // Read file content
        const content = await readFileAsText(file.file);

        // Validate first
        const validation = validateCubeFile(content);
        if (!isValidationValid(validation)) {
          updateFileStatus(file.id, 'failed', validation.message);
          failed++;
          completed++;
          setProgress((prev) => ({ ...prev, completed }));
          continue;
        }

        // Track metadata from validation
        updateFileStatus(file.id, 'converting', undefined, {
          lutSize: validation.lut_size,
          title: validation.title,
        });

        // Convert
        const convertResult = convertContent(
          content,
          file.filename,
          {
            name: options.name || file.filename.replace(/\.cube$/i, ''),
            ...(options.lutSize !== undefined ? { lutSize: options.lutSize } : {}),
          }
        );

        if (!isConvertSuccess(convertResult)) {
          updateFileStatus(file.id, 'failed', convertResult.message);
          failed++;
        } else {
          // Store result in store
          setFileResult(file.id, {
            xmpContent: convertResult.xmp_content,
            warnings: convertResult.warnings,
          });
          updateFileStatus(file.id, 'complete');

          // Add to ZIP list
          convertedFiles.push({
            name: generateOutputFilename(file.filename),
            content: convertResult.xmp_content,
          });
          succeeded++;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        updateFileStatus(file.id, 'failed', message);
        failed++;
      }

      completed++;
      setProgress((prev) => ({ ...prev, completed }));
    }

    // Create ZIP if any files succeeded
    let zipBlob: Blob | null = null;
    if (convertedFiles.length > 0) {
      zipBlob = createZip(convertedFiles);
    }

    const zipFilename = generateZipFilename();

    setResult({
      succeeded,
      failed,
      zipBlob,
      zipFilename,
    });

    setProgress((prev) => ({ ...prev, currentFile: null }));
    setIsConverting(false);
    completeJob();
  }, [files, options, updateFileStatus, setFileResult, startJob, completeJob]);

  const reset = useCallback(() => {
    setProgress({ completed: 0, total: 0, currentFile: null });
    setIsConverting(false);
    setResult(null);
  }, []);

  return {
    progress,
    isConverting,
    result,
    convertAll,
    reset,
  };
}

export default useBulkConversion;
